import { useEffect, useId, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { Icon } from '../Icon/Icon';
import { nb } from '@/i18n/nb';
import s from './Modal.module.css';

type ModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
};

export function Modal({ open, onClose, title, children }: ModalProps) {
  const titleId = useId();
  const trapRef = useFocusTrap<HTMLDivElement>(open);

  // Esc closes from anywhere while open; listener is document-level so it works
  // even if focus has drifted. Cleaned up when the modal unmounts/closes.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className={s.backdrop}
      onPointerDown={(e) => {
        // Backdrop click closes; clicks inside the panel don't bubble here.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={trapRef}
        className={s.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <button type="button" className={s.close} onClick={onClose} aria-label={nb.common.close}>
          <Icon name="x" size={20} />
        </button>
        <h2 id={titleId} className={s.title}>
          {title}
        </h2>
        {children}
      </div>
    </div>,
    document.body,
  );
}
